import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { useNavigate } from 'react-router-dom'
import { Play, Loader2, Settings, ChevronDown, Calendar, Save, BookOpen, RotateCcw, SlidersHorizontal, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { executionTemplatesApi } from '@/services/api/execution-templates'
import type { ExecutionTemplate, ExecutionTemplateConfig } from '@/types/execution-template'
import { useJobStore } from '@/store/jobStore'
import { useQuery } from '@tanstack/react-query'
import { datasetsApi } from '@/services/api/datasets'
import apiClient from '@/services/api/client'

interface ExecutionConfig {
  datasetId: string
  outputFormat: 'json' | 'csv' | 'parquet'
  limitRows?: number
  skipValidation: boolean
  includeMetadata: boolean
  executionMode: 'async' | 'sync'
  sampleData: boolean
  timeoutMinutes?: number
  customInputs?: Record<string, any>
  dateFilter?: {
    enabled: boolean
    startDate?: string
    endDate?: string
    preset?: 'all_time' | 'today' | 'yesterday' | 'this_week' | 'last_5_days' | 'last_10_days' | 'last_20_days' | 'this_month' | 'last_3_months' | 'last_6_months' | 'this_year' | 'last_year' | 'last_1_year'
  }
}

interface ExecuteButtonProps {
  scriptId: string
  dslCode?: string // DSL code to extract input parameters
  selectedDatasetId?: string // Pre-selected dataset ID
  onDatasetChange?: (datasetId: string) => void // Sync modal dataset selection to main UI
  selectedSymbol?: string // Selected symbol for partitioned datasets
  /** Toolbar date range: when modal opens, pre-fill date filter with these */
  initialDateStart?: string
  initialDateEnd?: string
  initialDatePreset?: string
  variant?: 'default' | 'outline' | 'ghost'
  disabled?: boolean
  hasUnsavedChanges?: boolean
  isNew?: boolean
  onSaveBeforeRun?: () => Promise<string> // Callback to save script before running, returns scriptId
  onJobCreated?: (jobId: string) => void // Callback when job is created
}

export interface ExecuteButtonHandle {
  openModal: () => void
}

const ExecuteButton = forwardRef<ExecuteButtonHandle, ExecuteButtonProps>(function ExecuteButton({
  scriptId,
  dslCode,
  selectedDatasetId: propSelectedDatasetId = '',
  onDatasetChange,
  selectedSymbol,
  initialDateStart,
  initialDateEnd,
  initialDatePreset,
  variant = 'default',
  disabled = false,
  hasUnsavedChanges = false,
  isNew = false,
  onSaveBeforeRun,
  onJobCreated
}, ref) {
  // Extract input parameters from DSL code
  const extractInputsFromDSL = (code: string) => {
    const inputs: Record<string, any> = {}
    const inputRegex = /input\s+(\w+)\s*=\s*([^;\n]+)/g
    let match

    while ((match = inputRegex.exec(code)) !== null) {
      const [, name, valueStr] = match
      try {
        // Try to parse as number first, then as other literals
        const trimmed = valueStr.trim()
        if (/^\d+(\.\d+)?$/.test(trimmed)) {
          inputs[name] = parseFloat(trimmed)
        } else if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
          inputs[name] = trimmed.slice(1, -1)
        } else if (trimmed === 'true') {
          inputs[name] = true
        } else if (trimmed === 'false') {
          inputs[name] = false
        } else {
          // Keep as string for complex expressions
          inputs[name] = trimmed
        }
      } catch (e) {
        console.warn(`Could not parse input value for ${name}: ${valueStr}`)
      }
    }

    return inputs
  }

  // Extract default inputs from DSL code
  const defaultInputs = dslCode ? extractInputsFromDSL(dslCode) : {}

  console.log('🔧 Extracted inputs from DSL:', defaultInputs)

  const navigate = useNavigate()
  const { createJob } = useJobStore()
  const [open, setOpen] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showCustomInputs, setShowCustomInputs] = useState(false)
  const [executionSymbol, setExecutionSymbol] = useState<string>(selectedSymbol || '')
  const [localSelectedDatasetId, setLocalSelectedDatasetId] = useState<string>(propSelectedDatasetId)

  // Update customInputs when selectedSymbol changes (editor context)
  useEffect(() => {
    setExecutionConfig(prev => ({
      ...prev,
      customInputs: {
        ...prev.customInputs,
        ...(selectedSymbol ? { selectedSymbol } : { selectedSymbol: undefined })
      }
    }))
  }, [selectedSymbol])

  const [executionConfig, setExecutionConfig] = useState<ExecutionConfig>({
    datasetId: propSelectedDatasetId || '',
    outputFormat: 'json',
    limitRows: undefined,
    skipValidation: false,
    includeMetadata: true,
    executionMode: 'async',
    sampleData: false,
    timeoutMinutes: undefined,
    customInputs: {
      ...defaultInputs,
      ...(selectedSymbol && { selectedSymbol }) // Add selected symbol if available
    },
    dateFilter: {
      enabled: false,
      preset: undefined,
      startDate: undefined,
      endDate: undefined,
    },
  })

  useImperativeHandle(ref, () => ({
    openModal: () => setOpen(true),
  }), [])

  // Trading-focused date presets
  const tradingDatePresets = [
    { id: 'all_time', name: 'All time', description: 'Full history, no date filter' },
    { id: 'today', name: 'Today', description: 'Current trading day' },
    { id: 'yesterday', name: 'Yesterday', description: 'Previous trading day' },
    { id: 'this_week', name: 'This Week', description: 'Current trading week (Mon-Fri)' },
    { id: 'last_5_days', name: 'Last 5 Days', description: 'Last 5 trading days' },
    { id: 'last_10_days', name: 'Last 10 Days', description: 'Last 10 trading days' },
    { id: 'last_20_days', name: 'Last 20 Days', description: 'Last 20 trading days' },
    { id: 'this_month', name: 'This Month', description: 'Current calendar month' },
    { id: 'last_3_months', name: 'Last 3 Months', description: 'Last 90 days' },
    { id: 'last_6_months', name: 'Last 6 Months', description: 'Last 180 days' },
    { id: 'this_year', name: 'This Year', description: 'Current calendar year' },
    { id: 'last_year', name: 'Last Year', description: 'Previous calendar year' },
    { id: 'last_1_year', name: 'Last 1 Year', description: 'Last 365 days' },
  ]
  const [isExecuting, setIsExecuting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Template management
  const [executionTemplates, setExecutionTemplates] = useState<ExecutionTemplate[]>([])
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [templateAction, setTemplateAction] = useState<'create' | 'update'>('create')
  const [selectedTemplateToUpdate, setSelectedTemplateToUpdate] = useState<string>('')

  // Use prop if provided, otherwise use local state
  const selectedDatasetId = propSelectedDatasetId || localSelectedDatasetId

  const { data: datasetsData } = useQuery({
    queryKey: ['datasets'],
    queryFn: async () => {
      const result = await datasetsApi.list()
      return result.datasets
    },
  })

  const readyDatasets = datasetsData?.filter((d) => d.status === 'ready') || []

  const selectedDatasetInModal = executionConfig.datasetId
    ? readyDatasets.find(d => d.id === executionConfig.datasetId)
    : null
  const isPartitionedDataset = !!(
    selectedDatasetInModal &&
    (String((selectedDatasetInModal.metadata as any)?.partitionStrategy) === 'by_symbol' ||
     String((selectedDatasetInModal.metadata as any)?.partitionType) === 'symbol')
  )
  const availableSymbolsInModal: string[] =
    isPartitionedDataset && selectedDatasetInModal
      ? ((selectedDatasetInModal.metadata as any)?.symbols as string[]) ||
        ((selectedDatasetInModal.metadata as any)?.syncMetadata?.symbols as string[]) ||
        []
      : []
  const showSymbolSelector = Boolean(
    executionConfig.datasetId &&
    selectedDatasetInModal &&
    isPartitionedDataset &&
    availableSymbolsInModal.length > 0
  )

  useEffect(() => {
    if (!open) return
    if (showSymbolSelector) {
      const preferred = selectedSymbol && availableSymbolsInModal.includes(selectedSymbol)
        ? selectedSymbol
        : (executionSymbol && availableSymbolsInModal.includes(executionSymbol) ? executionSymbol : null)
      setExecutionSymbol(preferred || availableSymbolsInModal[0] || '')
    } else {
      setExecutionSymbol(selectedSymbol || '')
    }
  }, [open, executionConfig.datasetId, showSymbolSelector, selectedSymbol])

  // Calculate date range from preset (trading-focused)
  const calculateDateRange = (preset: string) => {
    const now = new Date()
    let startDate: Date | undefined
    let endDate: Date | undefined

    switch (preset) {
      case 'all_time':
        // No date filter
        break
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'yesterday':
        const yesterday = new Date(now)
        yesterday.setDate(yesterday.getDate() - 1)
        startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())
        endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())
        break
      case 'this_week':
        // Monday to current day (trading week)
        const weekStart = new Date(now)
        const dayOfWeek = now.getDay() // 0 = Sunday, 1 = Monday, etc.
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // If Sunday, go back 6 days to Monday
        weekStart.setDate(now.getDate() - daysFromMonday)
        startDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_5_days':
        const last5Days = new Date(now)
        last5Days.setDate(now.getDate() - 5)
        startDate = new Date(last5Days.getFullYear(), last5Days.getMonth(), last5Days.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_10_days':
        const last10Days = new Date(now)
        last10Days.setDate(now.getDate() - 10)
        startDate = new Date(last10Days.getFullYear(), last10Days.getMonth(), last10Days.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_20_days':
        const last20Days = new Date(now)
        last20Days.setDate(now.getDate() - 20)
        startDate = new Date(last20Days.getFullYear(), last20Days.getMonth(), last20Days.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_3_months':
        const last3Months = new Date(now)
        last3Months.setMonth(now.getMonth() - 3)
        startDate = new Date(last3Months.getFullYear(), last3Months.getMonth(), last3Months.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_6_months':
        const last6Months = new Date(now)
        last6Months.setMonth(now.getMonth() - 6)
        startDate = new Date(last6Months.getFullYear(), last6Months.getMonth(), last6Months.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'this_year':
        startDate = new Date(now.getFullYear(), 0, 1)
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last_year':
        startDate = new Date(now.getFullYear() - 1, 0, 1)
        endDate = new Date(now.getFullYear() - 1, 11, 31)
        break
      case 'last_1_year':
        const y1 = new Date(now)
        y1.setFullYear(now.getFullYear() - 1)
        startDate = new Date(y1.getFullYear(), y1.getMonth(), y1.getDate())
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
    }

    // Format dates as YYYY-MM-DD strings
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: startDate ? formatDate(startDate) : undefined,
      endDate: endDate ? formatDate(endDate) : undefined
    };
  }


  // Update execution config when dataset changes
  const updateExecutionConfig = (updates: Partial<ExecutionConfig>) => {
    setExecutionConfig(prev => ({ ...prev, ...updates }))
  }

  // Update custom inputs
  const updateCustomInput = (name: string, value: any) => {
    setExecutionConfig(prev => ({
      ...prev,
      customInputs: {
        ...prev.customInputs,
        [name]: value
      }
    }))
  }

  // Reset custom inputs to default values
  const resetCustomInputs = () => {
    setExecutionConfig(prev => ({
      ...prev,
      customInputs: { ...defaultInputs }
    }))
  }

  // Update date filter
  const updateDateFilter = (updates: Partial<NonNullable<ExecutionConfig['dateFilter']>>) => {
    setExecutionConfig(prev => ({
      ...prev,
      dateFilter: prev.dateFilter ? { ...prev.dateFilter, ...updates } : {
        enabled: false,
        preset: undefined,
        startDate: undefined,
        endDate: undefined,
        ...updates
      }
    }))
  }

  // Sync selected dataset with execution config
  useEffect(() => {
    if (selectedDatasetId && selectedDatasetId !== executionConfig.datasetId) {
      updateExecutionConfig({ datasetId: selectedDatasetId })
    }
  }, [selectedDatasetId, executionConfig.datasetId])

  const prevOpenRef = useRef(false)
  // When modal opens, sync date filter from toolbar (initialDateStart/initialDateEnd/initialDatePreset)
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current
    prevOpenRef.current = open
    if (!justOpened) return
    if (initialDatePreset === 'all_time') {
      updateDateFilter({ enabled: false, preset: 'all_time', startDate: undefined, endDate: undefined })
      return
    }
    if (initialDateStart || initialDateEnd) {
      updateDateFilter({
        enabled: true,
        startDate: initialDateStart || undefined,
        endDate: initialDateEnd || undefined,
        preset: (initialDatePreset as NonNullable<ExecutionConfig['dateFilter']>['preset']) || undefined,
      })
    }
  }, [open, initialDateStart, initialDateEnd, initialDatePreset])

  // Load execution templates (skip scriptId when 'new' — backend expects UUID)
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const result = await executionTemplatesApi.list({
          ...(scriptId && scriptId !== 'new' ? { scriptId } : {}),
          limit: 50
        })
        setExecutionTemplates(result.templates)
      } catch (error) {
        console.warn('Failed to load execution templates:', error)
      }
    }

    loadTemplates()
  }, [scriptId])

  const toTemplateConfig = (config: ExecutionConfig): ExecutionTemplateConfig => {
    const df = config.dateFilter
    if (!df) return { ...config } as ExecutionTemplateConfig
    type TemplatePreset = NonNullable<NonNullable<ExecutionTemplateConfig['dateFilter']>['preset']>
    const templatePreset: TemplatePreset | undefined =
      df.preset === 'all_time' || df.preset === 'last_1_year' ? undefined : (df.preset as TemplatePreset)
    const dateFilter: ExecutionTemplateConfig['dateFilter'] = {
      enabled: df.enabled,
      startDate: df.startDate,
      endDate: df.endDate,
      preset: templatePreset,
    }
    return { ...config, dateFilter } as ExecutionTemplateConfig
  }

  const saveAsTemplate = async () => {
    if (templateAction === 'create' && !templateName.trim()) return
    if (templateAction === 'update' && !selectedTemplateToUpdate) return

    setIsSavingTemplate(true)
    try {
      const templateConfig = toTemplateConfig(executionConfig)
      if (templateAction === 'create') {
        await executionTemplatesApi.create({
          scriptId,
          name: templateName.trim(),
          description: templateDescription.trim() || undefined,
          config: templateConfig,
        })
        showSuccessToast('Template created successfully')
      } else {
        await executionTemplatesApi.update(selectedTemplateToUpdate, {
          name: templateName.trim() || undefined,
          description: templateDescription.trim() || undefined,
          config: templateConfig,
        })
        showSuccessToast('Template updated successfully')
      }

      // Reload templates
      const result = await executionTemplatesApi.list({
        scriptId,
        limit: 50
      })
      setExecutionTemplates(result.templates)

      setShowSaveTemplate(false)
      setTemplateName('')
      setTemplateDescription('')
      setTemplateAction('create')
      setSelectedTemplateToUpdate('')
    } catch (error) {
      console.error('Failed to save template:', error)
      showErrorToast(error instanceof Error ? error : new Error('Failed to save template'))
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const loadTemplate = (template: ExecutionTemplate) => {
    // Convert template config to execution config format
    const config = { ...template.config } as any
    updateExecutionConfig(config)
  }

  const handleSaveTemplateDialogClose = (open: boolean) => {
    setShowSaveTemplate(open)
    if (!open) {
      // Reset form state when closing
      setTemplateName('')
      setTemplateDescription('')
      setTemplateAction('create')
      setSelectedTemplateToUpdate('')
    }
  }

  const handleExecute = async (config?: Partial<ExecutionConfig>) => {
    const finalConfig = config ? { ...executionConfig, ...config } : executionConfig
    const finalDatasetId = finalConfig.datasetId || selectedDatasetId

    if (!finalDatasetId) {
      // If no dataset selected, open dialog
      setOpen(true)
      return
    }

    // Validate that the selected dataset still exists
    const datasetExists = readyDatasets.some(d => d.id === finalDatasetId)
    if (!datasetExists) {
      showErrorToast(new Error(`Selected dataset no longer exists. Please select a different dataset.`))
      // Clear the invalid selection
      if (propSelectedDatasetId) {
        // If it's a prop, we can't clear it, but we can show error
      } else {
        setLocalSelectedDatasetId('')
        updateExecutionConfig({ datasetId: '' })
      }
      return
    }

    if (finalConfig.dateFilter?.enabled && !finalConfig.dateFilter?.startDate?.trim()) {
      showErrorToast(new Error('Start date is required when Filter data by date range is active.'))
      return
    }

    setIsExecuting(true)
    setIsSaving(false)
    
    try {
      let finalScriptId = scriptId
      
      // Save script first if needed
      if ((hasUnsavedChanges || isNew) && onSaveBeforeRun) {
        setIsSaving(true)
        try {
          finalScriptId = await onSaveBeforeRun()
          if (!finalScriptId || finalScriptId === 'new') {
            throw new Error('Failed to save script before execution')
          }
        } catch (error) {
          console.error('Failed to save script before execution:', error)
          setIsExecuting(false)
          setIsSaving(false)
          return
        }
        setIsSaving(false)
      }
      
      // Validate scriptId
      if (!finalScriptId || finalScriptId === 'new') {
        throw new Error('Script must be saved before execution')
      }

      // Additional validation: script should still exist
      // This is a safety check in case the script was deleted while editing
      try {
        await apiClient.get(`/scripts/${finalScriptId}`)
        // If we get here, the script exists and user has access
      } catch (error: any) {
        if (error.response?.status === 404) {
          throw new Error('Script no longer exists. Please save your script first.')
        } else if (error.response?.status === 403) {
          throw new Error('You do not have access to this script.')
        } else {
          throw new Error('Script validation failed. Please try again.')
        }
      }

      const job = await createJob('execute', {
        scriptId: finalScriptId,
        datasetId: finalDatasetId,
        options: {
          outputFormat: finalConfig.outputFormat,
          limitRows: finalConfig.limitRows,
          skipValidation: finalConfig.skipValidation,
          includeMetadata: finalConfig.includeMetadata,
          executionMode: finalConfig.executionMode,
          sampleData: finalConfig.sampleData,
          timeoutMinutes: finalConfig.timeoutMinutes,
          selectedSymbol: executionSymbol || selectedSymbol || undefined,
          customInputs: {
            ...finalConfig.customInputs,
            ...(executionSymbol ? { selectedSymbol: executionSymbol } : {}),
          },
          dateFilter: finalConfig.dateFilter?.enabled ? {
            startDate: finalConfig.dateFilter.startDate,
            endDate: finalConfig.dateFilter.endDate,
          } : undefined,
        },
      })
      setOpen(false)

      // Save to localStorage
      localStorage.setItem('lastSelectedDatasetId', finalDatasetId)
      
      // Call callback if provided (for inline results)
      if (onJobCreated) {
        onJobCreated(job.id)
      } else {
        // Default behavior: navigate to job page
        navigate(`/jobs/${job.id}`)
      }
    } catch (error) {
      console.error('Failed to execute job:', error)
      showErrorToast(error, 'Failed to execute job. Please try again.')
    } finally {
      setIsExecuting(false)
      setIsSaving(false)
    }
  }

  // If dataset is pre-selected, execute directly on click
  const handleClick = () => {
    if (selectedDatasetId) {
      handleExecute({ ...executionConfig, datasetId: selectedDatasetId })
    } else {
      setOpen(true)
    }
  }

  return (
    <>
      <div className="flex items-center gap-1">
        <Button
          variant={variant}
          disabled={disabled || (!executionConfig.datasetId && readyDatasets.length === 0)}
          onClick={handleClick}
          className="rounded-r-none"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : isExecuting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              {hasUnsavedChanges || isNew ? 'Save & Run' : 'Run'}
            </>
          )}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={variant}
              disabled={disabled || (!executionConfig.datasetId && readyDatasets.length === 0)}
              className="rounded-l-none border-l-0 px-2"
              size="sm"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem
              onClick={() => setShowSaveTemplate(true)}
              className="flex items-center gap-3 p-3 cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <div>
                <div className="font-medium text-sm">Save as Template</div>
                <div className="text-xs text-muted-foreground">Save current config</div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setOpen(true)}
              className="flex items-center gap-3 p-3 cursor-pointer"
            >
              <Settings className="w-4 h-4" />
              <div>
                <div className="font-medium text-sm">Advanced Config</div>
                <div className="text-xs text-muted-foreground">Custom execution options</div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {hasUnsavedChanges || isNew ? 'Save & Execute Script' : 'Execute Script'}
            </DialogTitle>
            <DialogDescription>
              {(hasUnsavedChanges || isNew) && (
                <span className="text-amber-600 dark:text-amber-400 block mb-2">
                  ⚠️ Your script will be saved before execution
                </span>
              )}
              Configure execution options and select a dataset
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Options */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dataset">Dataset</Label>
                <Select
                  value={executionConfig.datasetId}
                  onValueChange={(value) => {
                    updateExecutionConfig({ datasetId: value })
                    onDatasetChange?.(value)
                  }}
                >
                  <SelectTrigger id="dataset">
                    <SelectValue placeholder="Select a dataset" />
                  </SelectTrigger>
                  <SelectContent>
                    {readyDatasets.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        No datasets available
                      </div>
                    ) : (
                      readyDatasets.map((dataset) => (
                        <SelectItem key={dataset.id} value={dataset.id}>
                          {dataset.name} {dataset.rowCount != null ? `(${dataset.rowCount.toLocaleString()} rows)` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {showSymbolSelector && (
                <div className="space-y-2">
                  <Label htmlFor="executionSymbol">Symbol</Label>
                  <Select
                    value={executionSymbol || ''}
                    onValueChange={(value) => setExecutionSymbol(value)}
                  >
                    <SelectTrigger id="executionSymbol">
                      <SelectValue placeholder="Select symbol" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSymbolsInModal.map((symbol) => (
                        <SelectItem key={symbol} value={symbol}>
                          {symbol}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Run script for this symbol only (dataset is partitioned by symbol)
                  </p>
                </div>
              )}
            </div>

            {/* Date Range — prominent section, always visible */}
            <div className="rounded-lg border-2 border-primary/20 bg-primary/5 dark:bg-primary/10 p-4 space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <Calendar className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold tracking-tight">Date range</h3>
                  <p className="text-xs text-muted-foreground">
                    Restrict execution to a specific period
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enableDateFilter"
                  checked={executionConfig.dateFilter?.enabled || false}
                  onCheckedChange={(checked) => updateDateFilter({
                    enabled: checked as boolean,
                    preset: checked ? 'last_5_days' : undefined
                  })}
                />
                <Label htmlFor="enableDateFilter" className="text-sm font-medium cursor-pointer">
                  Filter data by date range
                </Label>
                {executionConfig.dateFilter?.enabled && (
                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium">
                    Active
                  </span>
                )}
              </div>
              {executionConfig.dateFilter?.enabled && (
                <div className="space-y-3 pt-2 border-t border-border/60">
                  <div className="space-y-2">
                    <Label htmlFor="datePreset" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Quick presets
                    </Label>
                    <Select
                      value={executionConfig.dateFilter.preset || 'custom'}
                      onValueChange={(value: string) => {
                        if (value === 'custom') {
                          updateDateFilter({ preset: undefined })
                          return
                        }
                        if (value === 'all_time') {
                          updateDateFilter({ enabled: false, preset: 'all_time', startDate: undefined, endDate: undefined })
                          return
                        }
                        const dateRange = calculateDateRange(value)
                        updateDateFilter({
                          preset: value as any,
                          startDate: dateRange.startDate,
                          endDate: dateRange.endDate,
                        })
                      }}
                    >
                      <SelectTrigger id="datePreset" className="bg-background">
                        <SelectValue placeholder="Select trading period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">
                          <div>
                            <div className="font-medium">Custom</div>
                            <div className="text-xs text-muted-foreground">Enter start and end dates manually</div>
                          </div>
                        </SelectItem>
                        {tradingDatePresets.map((preset) => (
                          <SelectItem key={preset.id} value={preset.id}>
                            <div>
                              <div className="font-medium">{preset.name}</div>
                              <div className="text-xs text-muted-foreground">{preset.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="startDate" className="text-xs font-medium">
                        Start date <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="startDate"
                        type="date"
                        className="bg-background"
                        value={executionConfig.dateFilter.startDate || ''}
                        onChange={(e) => updateDateFilter({
                          startDate: e.target.value || undefined,
                          preset: undefined
                        })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="endDate" className="text-xs font-medium">End date</Label>
                      <Input
                        id="endDate"
                        type="date"
                        className="bg-background"
                        value={executionConfig.dateFilter.endDate || ''}
                        onChange={(e) => updateDateFilter({
                          endDate: e.target.value || undefined,
                          preset: undefined
                        })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Only rows within this range will be processed.
                  </p>
                </div>
              )}
            </div>

            {/* Custom Input Parameters — own section when script has inputs, collapsed by default */}
            {Object.keys(defaultInputs).length > 0 && (
              <div className="rounded-lg border-2 border-primary/20 bg-primary/5 dark:bg-primary/10 p-4 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                      <SlidersHorizontal className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold tracking-tight">Custom Input Parameters</h3>
                      <p className="text-xs text-muted-foreground">
                        {Object.keys(defaultInputs).length} parameter{Object.keys(defaultInputs).length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCustomInputs(!showCustomInputs)}
                    className="h-8 px-2"
                  >
                    {showCustomInputs ? (
                      <ChevronDown className="h-4 w-4 mr-1" />
                    ) : (
                      <ChevronRight className="h-4 w-4 mr-1" />
                    )}
                    {showCustomInputs ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {showCustomInputs && (
                <div className="space-y-3 pt-2 border-t border-border/60">
                  {Object.entries(defaultInputs).map(([name, defaultValue]) => (
                    <div key={name} className="space-y-2">
                      <Label htmlFor={`input-${name}`} className="text-sm font-medium">
                        {name}
                      </Label>
                      {typeof defaultValue === 'number' ? (
                        <Input
                          id={`input-${name}`}
                          type="number"
                          value={executionConfig.customInputs?.[name] ?? defaultValue}
                          onChange={(e) => updateCustomInput(name, parseFloat(e.target.value) || 0)}
                          className="h-8 bg-background"
                        />
                      ) : typeof defaultValue === 'boolean' ? (
                        <div className="flex items-center space-x-2">
                          <input
                            id={`input-${name}`}
                            type="checkbox"
                            checked={executionConfig.customInputs?.[name] ?? defaultValue}
                            onChange={(e) => updateCustomInput(name, e.target.checked)}
                            className="rounded"
                          />
                          <Label htmlFor={`input-${name}`} className="text-sm">
                            {executionConfig.customInputs?.[name] ?? defaultValue ? 'True' : 'False'}
                          </Label>
                        </div>
                      ) : (
                        <Input
                          id={`input-${name}`}
                          type="text"
                          value={executionConfig.customInputs?.[name] ?? defaultValue}
                          onChange={(e) => updateCustomInput(name, e.target.value)}
                          className="h-8 bg-background"
                        />
                      )}
                      <p className="text-xs text-muted-foreground">
                        Default: {String(defaultValue)}
                      </p>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resetCustomInputs}
                      className="h-8 text-xs"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Reset to Defaults
                    </Button>
                  </div>
                </div>
                )}
              </div>
            )}

            {/* Advanced Options Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Advanced Options</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="h-8 px-2"
              >
                <Settings className="w-4 h-4 mr-1" />
                {showAdvanced ? 'Hide' : 'Show'}
              </Button>
            </div>

            {/* Advanced Options */}
            {showAdvanced && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="outputFormat">Output Format</Label>
                      <Select
                        value={executionConfig.outputFormat}
                        onValueChange={(value: 'json' | 'csv' | 'parquet') => updateExecutionConfig({ outputFormat: value })}
                      >
                        <SelectTrigger id="outputFormat">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="json">JSON</SelectItem>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="parquet">Parquet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="executionMode">Execution Mode</Label>
                      <Select
                        value={executionConfig.executionMode}
                        onValueChange={(value: 'async' | 'sync') => updateExecutionConfig({ executionMode: value })}
                      >
                        <SelectTrigger id="executionMode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="async">Async (Background)</SelectItem>
                          <SelectItem value="sync">Sync (Wait for result)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="limitRows">Limit Rows (Optional)</Label>
                    <Input
                      id="limitRows"
                      type="number"
                      placeholder="e.g. 1000"
                      value={executionConfig.limitRows || ''}
                      onChange={(e) => updateExecutionConfig({
                        limitRows: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      min="1"
                    />
                    <p className="text-xs text-muted-foreground">
                      Limit the number of rows to process (useful for testing)
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="skipValidation"
                        checked={executionConfig.skipValidation}
                        onCheckedChange={(checked) =>
                          updateExecutionConfig({ skipValidation: checked as boolean })
                        }
                      />
                      <Label htmlFor="skipValidation" className="text-sm">
                        Skip input validation
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="includeMetadata"
                        checked={executionConfig.includeMetadata}
                        onCheckedChange={(checked) =>
                          updateExecutionConfig({ includeMetadata: checked as boolean })
                        }
                      />
                      <Label htmlFor="includeMetadata" className="text-sm">
                        Include execution metadata in results
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sampleData"
                        checked={executionConfig.sampleData}
                        onCheckedChange={(checked) =>
                          updateExecutionConfig({ sampleData: checked as boolean })
                        }
                      />
                      <Label htmlFor="sampleData" className="text-sm">
                        Use sample data for faster testing
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeoutMinutes">Timeout (minutes)</Label>
                    <Select
                      value={executionConfig.timeoutMinutes?.toString() || 'none'}
                      onValueChange={(value) => updateExecutionConfig({
                        timeoutMinutes: value === 'none' ? undefined : parseInt(value)
                      })}
                    >
                      <SelectTrigger id="timeoutMinutes">
                        <SelectValue placeholder="No timeout" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No timeout</SelectItem>
                        <SelectItem value="1">1 minute</SelectItem>
                        <SelectItem value="5">5 minutes</SelectItem>
                        <SelectItem value="10">10 minutes</SelectItem>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  <p className="text-xs text-muted-foreground">
                    Maximum execution time (for sync mode)
                    </p>
                  </div>

                </div>
              </>
            )}

            {/* Templates Section */}
            {executionTemplates.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary" />
                      <Label className="text-sm font-medium">Load Template</Label>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSaveTemplate(true)}
                      className="h-8 text-xs"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Save Current
                    </Button>
                  </div>

                  <Select onValueChange={(templateId) => {
                    const template = executionTemplates.find(t => t.id === templateId)
                    if (template) loadTemplate(template)
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template to load..." />
                    </SelectTrigger>
                    <SelectContent>
                      {executionTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center justify-between w-full">
                            <span className="font-medium">{template.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {template.description || 'No description'}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <p className="text-xs text-muted-foreground">
                    Templates contain saved execution configurations for quick reuse
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleExecute(executionConfig)}
              disabled={
                !executionConfig.datasetId ||
                isExecuting ||
                isSaving ||
                (!!executionConfig.dateFilter?.enabled && !executionConfig.dateFilter?.startDate?.trim())
              }
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : isExecuting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {hasUnsavedChanges || isNew ? 'Save & Execute' : 'Execute'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={showSaveTemplate} onOpenChange={handleSaveTemplateDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Execution Template</DialogTitle>
            <DialogDescription>
              Save your current execution configuration as a reusable template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Template Action Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Action</Label>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="create-template"
                    name="templateAction"
                    value="create"
                    checked={templateAction === 'create'}
                    onChange={(e) => {
                      setTemplateAction(e.target.value as 'create' | 'update')
                      setSelectedTemplateToUpdate('')
                    }}
                    className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <Label htmlFor="create-template" className="text-sm">Create New Template</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="update-template"
                    name="templateAction"
                    value="update"
                    checked={templateAction === 'update'}
                    onChange={(e) => {
                      setTemplateAction(e.target.value as 'create' | 'update')
                      setTemplateName('')
                    }}
                    className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    disabled={executionTemplates.length === 0}
                  />
                  <Label htmlFor="update-template" className="text-sm">Update Existing Template</Label>
                </div>
              </div>
            </div>

            {/* Template Selection for Update */}
            {templateAction === 'update' && (
              <div className="space-y-2">
                <Label htmlFor="existingTemplate">Select Template to Update</Label>
                <Select value={selectedTemplateToUpdate} onValueChange={setSelectedTemplateToUpdate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template to update" />
                  </SelectTrigger>
                  <SelectContent>
                    {executionTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                        {template.description && (
                          <span className="text-muted-foreground ml-2">- {template.description}</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Template Name for Create */}
            {templateAction === 'create' && (
              <div className="space-y-2">
                <Label htmlFor="templateName">Template Name</Label>
                <Input
                  id="templateName"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Quick Analysis, Production Run"
                />
              </div>
            )}

            {/* Template Name for Update (optional) */}
            {templateAction === 'update' && selectedTemplateToUpdate && (
              <div className="space-y-2">
                <Label htmlFor="templateName">New Template Name (Optional)</Label>
                <Input
                  id="templateName"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Leave empty to keep current name"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="templateDescription">Description (Optional)</Label>
              <Input
                id="templateDescription"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief description of this configuration"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveAsTemplate}
              disabled={
                (templateAction === 'create' && !templateName.trim()) ||
                (templateAction === 'update' && !selectedTemplateToUpdate) ||
                isSavingTemplate
              }
            >
              {isSavingTemplate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {templateAction === 'create' ? 'Create Template' : 'Update Template'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
})

export default ExecuteButton
